import Config from './messages';
import { generateUUID } from './shared/functions';
import {
	makeSystemModePath,
	setSystemToLowercase,
} from './shared/serverFunctions';

/**
 * Location Inventory
 * Repacks and Retail Boxes
 * Incoming Transfers / by Location
 * Received Transfers / by Location
 * Transfers by Memo
 * Transfer Contents
 * Transfer Audits
 * Missing Scans
 *
 * GET_LIST, GET_ONE, GET_MANY, GET_MANY_REFERENCE, CREATE, UPDATE, DELETE
 */
export const RESOURCES = {
	LocationInventory: {
		GET_LIST: function (params) {
			const system = setSystemToLowercase();
			const mode = localStorage.getItem('mode');
			const location_id = getLocationIdFromFilter(params);
			return {
				type: 'list',
				method: 'GET',
				title: 'Locations',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/locationInventory/' +
					system +
					'/' +
					location_id +
					'/' +
					mode +
					'?params=' +
					JSON.stringify(params),
			};
		},
		GET_ONE: function (params) {
			const system = setSystemToLowercase();
			const mode = localStorage.getItem('mode');
			return {
				type: 'item',
				method: 'GET',
				title: 'Location Inventory',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/locationInventory/' +
					system +
					'/' +
					params.id +
					'/' +
					mode,
			};
		},
		UPDATE: function (params) {
			let inventoryParams = populateInventoryAdjustParams(params);
			return {
				type: 'upsert',
				data: inventoryParams.data,
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/adminAdjustment',
			};
		},
		DELETE: function (params) {
			let inventoryParams = populateInventoryZeroOutParams(params);
			return {
				type: 'delete',
				data: inventoryParams.data,
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/locationInventoryZeroOut',
			};
		},
	},
	RepacksRetailBoxes: {
		GET_ONE: function (params) {
			let systemMode = makeSystemModePath();
			return {
				type: 'item',
				method: 'GET',
				title: 'Repack / Retail Box',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/lookupInventory/' +
					systemMode +
					'/' +
					params.id,
			};
		},
		UPDATE: function (params) {
			let repackRetailBoxParams = populateRepackRetailBoxParams(params);
			return {
				type: 'upsert',
				data: repackRetailBoxParams.data,
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/editCollection',
			};
		},
	},
	Transfers: {
		GET_ONE: function (params) {
			let systemMode = makeSystemModePath();
			return {
				type: 'item',
				method: 'GET',
				title: 'Transfer',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/lookupInventory/' +
					systemMode +
					'/' +
					params.id,
			};
		},
		CREATE: function (params) {
			let transferParams = populateTransferParams(params);
			return {
				type: 'upsert',
				data: transferParams.data,
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/createTransfer',
			};
		}
	},
	TransfersByLocation: {
		GET_LIST: function (params) {
			let systemMode = makeSystemModePath();
			const location_id = getLocationIdFromFilter(params);
			return {
				type: 'list',
				method: 'GET',
				title: 'Incoming Transfers',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/systemInventoryTransfers/' +
					systemMode +
					'/' +
					location_id +
					'?params=' +
					JSON.stringify(params),
			};
		}
	},
	ReceivedTransfersByLocation: {
		GET_LIST: function (params) {
			let systemMode = makeSystemModePath();
			const location_id = getLocationIdFromFilter(params);
			return {
				type: 'list',
				method: 'GET',
				title: 'Received Transfers',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/systemReceivedInventoryTransfers/' +
					systemMode +
					'/' +
					location_id +
					'?params=' +
					JSON.stringify(params),
			};
		}
	},
	TransferContents: {
		GET_ONE: function (params) {
			let systemMode = makeSystemModePath();
			return {
				type: 'list',
				method: 'GET',
				title: 'Transfer Contents',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/transferContents/' +
					systemMode +
					'/' +
					params.transfer_id,
			};
		},
	},
	TransferCollectionContents: {
		GET_LIST: function (params) {
			let systemMode = makeSystemModePath();
			let unitType = params.filter.unit_type;
			if (unitType) {
				params.unit_type = unitType;
			}
			let barcode = params.filter.barcode;
			if (barcode) {
				params.barcode = barcode;
			}
			return {
				type: 'list',
				method: 'GET',
				title: 'Collection Contents',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/collectionContents/' +
					systemMode +
					'/' +
					params.unit_type +
					'/' +
					params.barcode,
			};
		},
	},
	TransferChangeLocation: {
		UPDATE: function (params) {
			let systemMode = makeSystemModePath();
			return {
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/changeTransferLocation/' +
					systemMode +
					'/' +
					params.location +
					'/' +
					params.transfer_id,
			};
		},
	},
	TransferReceive: {
		UPDATE: function () {
			return {
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/receiveInventory',
			};
		},
	},
	TransferAudits: {
		GET_LIST: function (params) {
			let systemMode = makeSystemModePath();

			let uri;
			// Convert dates from filter Date objects
			let dateFrom = params.filter.date_from;
			if (dateFrom) {
				params.date_from = dateFrom.substr(0, 10);
			}
			let dateTo = params.filter.date_to;
			if (dateTo) {
				params.date_to = dateTo.substr(0, 10);
			}
			let offset = params.filter.offset;

			if (dateFrom && dateTo && offset) {
				uri =
					localStorage.getItem('server') +
					'/api/v5/admin/getInventoryTransferAuditsByDate/' +
					systemMode +
					'/' +
					params.date_from +
					'/' +
					params.date_to +
					'/' +
					offset;
			}
			return {
				type: 'list',
				method: 'GET',
				title: 'Transfer Audits',
				uri: uri,
			};
		},
		GET_ONE: function (params) {
			let systemMode = makeSystemModePath();
			return {
				type: 'item',
				method: 'GET',
				title: 'Transfer Audit',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/transferAudit/' +
					systemMode +
					'/' +
					params.id,
			};
		},
	},
	LocationAudits: {
		GET_LIST: function (params) {
			const system = setSystemToLowercase();
			return {
				type: 'list',
				method: 'GET',
				title: 'Locations with Audits',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/locationsWithAudits/' +
					system +
					'?params=' +
					JSON.stringify(params),
			};
		},
		GET_ONE: function (params) {
			let systemMode = makeSystemModePath();
			let id = params.filter.id;
			if (id) {
				params.id = id.toString();
			}
			return {
				type: 'item',
				method: 'GET',
				title: 'Location Audit',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/openAudit/' +
					systemMode +
					'/' +
					params.id,
			};
		},
		UPDATE: function () {
			// Currently unused until Audits can be updated
			return {
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/inventoryAuditSubmit',
			};
		},
	},
	LocationAuditContents: {
		GET_LIST: function (params) {
			let systemMode = makeSystemModePath();
			let location_id = params.filter.location_id;
			if (location_id) {
				params.location_id = location_id.toString();
			}
			let audit_uuid = params.filter.audit_uuid;
			if (audit_uuid) {
				params.audit_uuid = audit_uuid;
			}
			return {
				type: 'list',
				method: 'GET',
				title: 'Location Audit',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/AdminDomainManager/auditSubmittedItems/' +
					systemMode +
					'/' +
					params.location_id +
					'/' +
					params.audit_uuid +
					'?params=' +
					JSON.stringify(params),
			};
		},
	},
	LocationAuditCollection: {
		GET_LIST: function (params) {
			let systemMode = makeSystemModePath();
			let sequence = params.filter.sequence;
			if (sequence) {
				params.sequence = sequence.toString();
			}
			return {
				type: 'list',
				method: 'GET',
				title: 'Location Audit Collection Contents',
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/lookupInventory/' +
					systemMode +
					'/' +
					params.sequence +
					'?params=' +
					JSON.stringify(params),
			};
		},
	},
	LocationAuditFinalize: {
		UPDATE: function () {
			return {
				uri:
					localStorage.getItem('server') +
					'/api/v4/Domain/Manager/InventoryDomainManager/auditFinalize',
			};
		},
	}
};

/**
 * Get a Location ID from filter select or text input
 *
 * @param params
 * @return {null}
 */
const getLocationIdFromFilter = params => {
	if (!params.filter) {
		return null;
	}
	const location_id_select = params.filter.location_id_select
		? params.filter.location_id_select
		: null;
	let location_id = params.filter.location_id_text
		? params.filter.location_id_text
		: null;
	if (location_id_select) {
		location_id = location_id_select;
	}
	return location_id;
};

/**
 * Populate values for Inventory Adjustment
 *
 * @param params
 * @return {*}
 */
const populateInventoryAdjustParams = params => {
	params.data.system = setSystemToLowercase();
	params.data.mode = localStorage.getItem('mode');
	params.data.type = Config.fuse.inventory.adjustment_type;
	params.data.location = params.location_id;

	delete params.data.product_type;

	return params;
};

const populateInventoryZeroOutParams = params => {
	params.data = {};
	params.data.system = setSystemToLowercase();
	params.data.mode = localStorage.getItem('mode');
	params.data.location = params.id;
	params.data.origin_type = 'admin_zero_out';

	return params;
};

const populateRepackRetailBoxParams = params => {
	params.data.mode = localStorage.getItem('mode');
	params.data.system = encodeURIComponent(localStorage.getItem('system'));

	let modelContents = [];
	for (let c = 0; c < params.data.contents.length; c++) {
		let item = {
			barcode: params.data.contents[c].barcode,
			qty: params.data.contents[c].qty,
		};
		modelContents.push(item);
	}
	delete params.data.contents;
	params.data.contents = modelContents;

	delete params.data.barcode;
	delete params.data.id;
	delete params.data.type;
	delete params.data.inv_collection_model;
	delete params.data.add_product_unit_qty;
	delete params.data.product_unit_select;

	return params;
};

const populateTransferParams = params => {
	const clientUdt = {
		system: localStorage.getItem('system'),
		location: params.data.location_from.toString(),
		register: Config.fuse.client.register,
		user: localStorage.getItem('email'),
		device: Config.fuse.client.device,
		version: Config.fuse.admin.version,
	};
	params.data.inv_transfer_model = {
		system: encodeURIComponent(localStorage.getItem('system')),
		mode: localStorage.getItem('mode'),
		location_to: params.data.location_to.toString(),
		created_by_client: clientUdt,
	};
	params.data.type = 'transfer';

	params.data.transfer_items.forEach(function (item, idx) {
		delete item.contents;
		delete item.transfer_type;
	});
	params.data.id = generateUUID();

	delete params.data.location_from;
	delete params.data.location_from_dba;
	delete params.data.location_to;
	delete params.data.item_select_field;
	delete params.data.item_qty_field;

	return params;
};

export default (type, resource, params) => {
	/**
	 * @param {string} type Request type, e.g. GET_LIST
	 * @param {string} resource Resource name, e.g. "DBAs"
	 * @param {Object} payload Request parameters. Depends on request type
	 * @returns {Object} RESOURCES object
	 */
	return (type, resource, params) => {
		return RESOURCES;
	};
};



// WEBPACK FOOTER //
// ./src/Fuse/fuseInventoryResourceMap.js